import { h as __h } from "preact";
/** @import { Signal } from "@preact/signals"; */
import { Component, h } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
/** @import { paginatedResponse, query } from "~/lib/api.js"; */

/** @import { DropdownItem } from "~/component/Dropdown.js"; */
import Data from "~/component/Data.js";

/** @import Pagination from "~/admin/component/Pagination.js"; */
import { Trend } from "~/admin/component/StatsGraph.js";
import { MaintenanceChip, StatusChip } from "~/admin/component/StatusChip.js";
/** @import { FreshKey } from "~/admin/component/FreshKeyCallout.js"; */
/** @import { TableHeader } from "~/admin/component/Table.js"; */
import Table from "~/admin/component/Table.js";
import { deleteRelay } from "./actions/deleteRelay.js";
import { editRelay } from "./actions/editRelay.js";
import { regenerateKey } from "./actions/regenerateKey.js";
import adminStyle from "~/admin/lib/style.js";
import util from "~/style/util.js";
/** @import { relayResponse, relayStatsResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<RelayTableProps>}
 *
 * @typedef {object} RelayTableProps
 * @property {string} orgId
 * @property {ReturnType<
 * 	typeof query<`/v2/orgs/${string}/relays`, ReturnType<typeof paginatedResponse<relayResponse>>>
 * >} query
 * @property {Signal<FreshKey | null>} freshKey
 * @property {ComponentChildren} [footer]
 * @property {(h: TableHeader[]) => TableHeader[]} [headers]
 * @property {(
 * 	items: DropdownItem[],
 * 	relay: relayResponse,
 * 	anchor: RefObject<HTMLButtonElement>,
 * ) => DropdownItem[]} [onTripleDots]
 * @property {InstanceType<typeof Pagination.State>} [paginate]
 * @property {InstanceType<typeof Table.Sort>} [sort]
 * @property {false} [scroll]
 */
export default class RelayTable extends Component {
  /** @type {Map<string, { at: relayStatsResponse; before: relayStatsResponse | null }>} */
  #samples = new Map();

  /** @param {relayResponse} relay */
  #previous(relay) {
    if (!relay.stats) return null;
    const seen = this.#samples.get(relay.id);
    if (!seen) {
      this.#samples.set(relay.id, {
        at: relay.stats,
        before: null
      });
      return null;
    }
    if (seen.at.reported_at !== relay.stats.reported_at) {
      seen.before = seen.at;
      seen.at = relay.stats;
    }
    return seen.before;
  }
  get #headers() {
    const h = [{
      id: "name",
      label: "Name",
      sort: "name"
    }, {
      id: "status",
      label: "Status"
    }, {
      id: "version",
      label: "Version"
    }, {
      id: "connections",
      label: "Connections"
    }, {
      id: "uptime",
      label: "Uptime"
    }, {
      id: "bitrate",
      label: "Bitrate"
    }, {
      id: "cpu",
      label: "CPU"
    }, {
      id: "memory",
      label: "Memory"
    }];
    if (this.props.headers) return this.props.headers(h);
    return h;
  }

  /**
   * @returns {(
   * 	relay: relayResponse,
   * 	anchor: { current: HTMLButtonElement },
   * ) => DropdownItem[]}
   */
  get #onTripleDots() {
    const canEdit = identity.permission(this.props.orgId, "relays", "edit");
    return (relay, anchor) => {
      /** @type {DropdownItem[]} */
      const items = [];
      if (canEdit) {
        items.push({
          icon: "pencil",
          text: "Edit relay",
          onSelect: () => editRelay({
            orgId: this.props.orgId
          }, relay)
        }, {
          icon: "key",
          text: "Regenerate key",
          onSelect: async () => {
            const res = await regenerateKey({
              orgId: this.props.orgId
            }, relay);
            if (res) this.props.freshKey.value = res;
          }
        }, {
          icon: "trash",
          text: "Delete relay",
          kind: "negative",
          onSelect: () => deleteRelay({
            orgId: this.props.orgId
          }, relay)
        });
      }
      if (this.props.onTripleDots) return this.props.onTripleDots(items, relay, anchor);
      return items;
    };
  }
  render() {
    return h(Data, {
      loading: this.props.query.loading && !this.props.query.data,
      error: this.props.query.error,
      children: h(/** @type {typeof Table<relayResponse>} */Table, {
        label: "relay",
        headers: this.#headers,
        empty: "No relays found.",
        footer: this.props.footer,
        rows: this.props.query.data?.data.map(relay => {
          const stats = relay.stats;
          const prev = this.#previous(relay);
          return {
            key: relay.id,
            label: relay.name,
            value: relay,
            columns: {
              name: RelayTable.primary(relay),
              status: RelayTable.status(relay),
              version: RelayTable.version(relay),
              connections: RelayTable.connections(stats?.active_connections),
              uptime: RelayTable.uptime(relay),
              bitrate: RelayTable.bitrate(stats && (stats.client_bits_per_second + stats.host_bits_per_second) / 1e6, prev && (prev.client_bits_per_second + prev.host_bits_per_second) / 1e6),
              cpu: RelayTable.cpu(stats?.cpu_busy_percent, prev?.cpu_busy_percent),
              memory: RelayTable.memory(stats?.memory_percent, prev?.memory_percent)
            }
          };
        }) ?? [],
        paginate: this.props.paginate?.props(this.props.query),
        sort: this.props.sort?.props(),
        href: relay => `/org/${this.props.orgId}/relays/${relay.location_id}/${relay.id}`,
        onTripleDots: this.#onTripleDots,
        scroll: this.props.scroll
      })
    });
  }

  /** @param {relayResponse} relay */
  static primary(relay) {
    return __h("div", {
      "class": adminStyle.withDesc
    }, __h("span", {
      "class": util.truncate
    }, relay.name), __h("span", {
      "class": util.truncate + " " + adminStyle.desc
    }, relay.description));
  }

  /** @param {relayResponse} relay */
  static status(relay) {
    return [__h(StatusChip, {
      online: relay.is_online
    }), relay.is_maintenance_mode_enabled && __h(MaintenanceChip, {})];
  }

  /** @param {relayResponse} relay */
  static version(relay) {
    return relay?.stats?.version;
  }

  /** @param {number | null} [count] */
  static connections(count) {
    return count ?? "—";
  }

  /** @param {relayResponse} relay */
  static uptime(relay) {
    const uptime = relay.stats?.uptime_seconds ?? 0;
    const days = Math.floor(uptime / 86400);
    const hours = Math.floor(uptime / 3600) % 24;
    const minutes = Math.floor(uptime / 60) % 60;
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${uptime % 60}s`;
    if (minutes > 0) return `${minutes}m ${uptime % 60}s`;
    return `${uptime % 60}s`;
  }

  /**
   * @param {number | null} [mbps]
   * @param {number | null} [prev]
   */
  static bitrate(mbps, prev) {
    return [mbps != null && __h(Trend, {
      value: mbps,
      prev: prev
    }), mbps == null ? "— " : Number(mbps.toFixed(2)), "mbps"];
  }

  /**
   * @param {number | null} [percent]
   * @param {number | null} [prev]
   */
  static cpu(percent, prev) {
    return [percent != null && __h(Trend, {
      value: percent,
      prev: prev,
      polarity: "up-bad"
    }), percent ?? "— ", "%"];
  }

  /**
   * @param {number | null} [percent]
   * @param {number | null} [prev]
   */
  static memory(percent, prev) {
    return [percent != null && __h(Trend, {
      value: percent,
      prev: prev,
      polarity: "up-bad"
    }), percent ?? "— ", "%"];
  }

  /**
   * @param {number | null} [percent]
   * @param {number | null} [prev]
   */
  static disk(percent, prev) {
    return [percent != null && __h(Trend, {
      value: percent,
      prev: prev,
      polarity: "up-bad"
    }), percent ?? "— ", "%"];
  }

  /** @param {relayResponse} relay */
  static publicAddress(relay) {
    return `${relay.public_address}:${relay.public_port}`;
  }

  /** @param {relayResponse} relay */
  static privateAddress(relay) {
    return `${relay.private_address}:${relay.private_port}`;
  }
}